import * as THREE from 'three';

export interface FeeZone {
  minFee: number;
  maxFee: number;
  color: THREE.Color;
  radius: number;
  height: number;
  density: number;
}

export interface FeeZoneConfig {
  innerRadius: number;
  outerRadius: number;
  height: number;
}

export interface FeeHistogramEntry {
  feeRate: number;
  count: number;
}

export interface ZonePosition {
  radius: number;
  height: number;
  variance: THREE.Vector3;
}

export class FeeZoneManager extends THREE.Group {
  private config: FeeZoneConfig;
  
  // Zone definitions
  public zones: Map<string, FeeZone> = new Map();
  
  // Visual representation
  private zoneBoundaries: {
    visible: boolean;
    opacity: number;
    glowIntensity: number;
  } = {
    visible: true,
    opacity: 0.1,
    glowIntensity: 0.3,
  };
  
  // Gradient texture for fee visualization
  private feeGradientTexture: THREE.DataTexture;
  private zoneVisualization: THREE.Group;
  
  // Animation properties
  private time: number = 0;
  
  constructor(config: FeeZoneConfig) {
    super();
    
    this.config = config;
    this.userData = { id: 'fee-zone-manager', type: 'manager' };
    
    this.createFeeZones();
    this.createGradientTexture();
    this.createZoneVisualization();
  }
  
  private createFeeZones(): void {
    const { innerRadius, outerRadius, height } = this.config;
    
    // Define fee zones from inner to outer radius
    const zoneDefinitions = [
      {
        name: 'priority',
        minFee: 200,
        maxFee: Infinity,
        color: new THREE.Color(0xff4444),
        radiusRange: [innerRadius * 0.2, innerRadius * 0.6],
        heightRange: [height * 0.3, height * 0.7],
      },
      {
        name: 'high',
        minFee: 50,
        maxFee: 200,
        color: new THREE.Color(0xffaa44),
        radiusRange: [innerRadius * 0.6, innerRadius * 0.9],
        heightRange: [height * 0.2, height * 0.8],
      },
      {
        name: 'medium',
        minFee: 10,
        maxFee: 50,
        color: new THREE.Color(0x44ff88),
        radiusRange: [innerRadius * 0.9, outerRadius * 0.7],
        heightRange: [height * 0.1, height * 0.9],
      },
      {
        name: 'low',
        minFee: 0,
        maxFee: 10,
        color: new THREE.Color(0x4488ff),
        radiusRange: [outerRadius * 0.7, outerRadius],
        heightRange: [0, height],
      },
    ];
    
    for (const zoneDef of zoneDefinitions) {
      const zone: FeeZone = {
        minFee: zoneDef.minFee,
        maxFee: zoneDef.maxFee,
        color: zoneDef.color,
        radius: (zoneDef.radiusRange[0] + zoneDef.radiusRange[1]) / 2,
        height: (zoneDef.heightRange[0] + zoneDef.heightRange[1]) / 2,
        density: 0.5, // Will be updated based on fee distribution
      };
      
      this.zones.set(zoneDef.name, zone);
    }
  }
  
  private createGradientTexture(): void {
    const width = 256;
    const height = 1;
    const data = new Uint8Array(width * height * 4);
    
    for (let i = 0; i < width; i++) {
      const t = i / (width - 1);
      let color: THREE.Color;
      
      // Create gradient from low fees (blue) to high fees (red)
      if (t < 0.25) {
        // Low fees: blue to green
        color = new THREE.Color().lerpColors(
          new THREE.Color(0x4488ff),
          new THREE.Color(0x44ff88),
          t * 4
        );
      } else if (t < 0.75) {
        // Medium fees: green to orange
        color = new THREE.Color().lerpColors(
          new THREE.Color(0x44ff88),
          new THREE.Color(0xffaa44),
          (t - 0.25) * 2
        );
      } else {
        // High fees: orange to red
        color = new THREE.Color().lerpColors(
          new THREE.Color(0xffaa44),
          new THREE.Color(0xff4444),
          (t - 0.75) * 4
        );
      }
      
      const index = i * 4;
      data[index] = Math.floor(color.r * 255);
      data[index + 1] = Math.floor(color.g * 255);
      data[index + 2] = Math.floor(color.b * 255);
      data[index + 3] = 255;
    }
    
    this.feeGradientTexture = new THREE.DataTexture(
      data,
      width,
      height,
      THREE.RGBAFormat
    );
    this.feeGradientTexture.needsUpdate = true;
  }
  
  private createZoneVisualization(): void {
    this.zoneVisualization = new THREE.Group();
    this.zoneVisualization.userData = { id: 'zone-visualization', type: 'visualization' };
    
    // Create zone boundary rings
    for (const [zoneName, zone] of this.zones.entries()) {
      const ringGeometry = new THREE.RingGeometry(
        zone.radius * 0.9,
        zone.radius * 1.1,
        32
      );
      
      const ringMaterial = new THREE.MeshBasicMaterial({
        color: zone.color,
        transparent: true,
        opacity: this.zoneBoundaries.opacity,
        side: THREE.DoubleSide,
      });
      
      const ring = new THREE.Mesh(ringGeometry, ringMaterial);
      ring.rotation.x = -Math.PI / 2; // Lay flat
      ring.userData = { id: `zone-ring-${zoneName}`, zone: zoneName };
      
      this.zoneVisualization.add(ring);
    }
    
    this.add(this.zoneVisualization);
  }
  
  public getZonePosition(feeRate: number): ZonePosition {
    // Find the appropriate zone for this fee rate
    let targetZone: FeeZone | null = null;
    let zoneName = '';
    
    for (const [name, zone] of this.zones.entries()) {
      if (feeRate >= zone.minFee && feeRate < zone.maxFee) {
        targetZone = zone;
        zoneName = name;
        break;
      }
    }
    
    // Default to low fee zone if no match
    if (!targetZone) {
      targetZone = this.zones.get('low')!;
      zoneName = 'low';
    }
    
    // Calculate position within the zone
    const zoneProgress = this.getZoneProgress(feeRate, targetZone);
    
    // Add radial variance based on zone density
    const radiusVariance = targetZone.radius * 0.2 * (1 - targetZone.density);
    const baseRadius = targetZone.radius + (Math.random() - 0.5) * radiusVariance;
    
    // Height varies based on fee rate within zone
    const heightVariance = targetZone.height * 0.3;
    const baseHeight = (Math.random() - 0.5) * heightVariance;
    
    // Create variance vector for spawning
    const variance = new THREE.Vector3(
      (Math.random() - 0.5) * 3,
      (Math.random() - 0.5) * 2,
      (Math.random() - 0.5) * 3
    );
    
    return {
      radius: baseRadius,
      height: baseHeight,
      variance,
    };
  }
  
  private getZoneProgress(feeRate: number, zone: FeeZone): number {
    if (zone.maxFee === Infinity) {
      // For priority zone, use logarithmic scale
      return Math.min(1, Math.log(feeRate / zone.minFee + 1) / Math.log(10));
    }
    
    return (feeRate - zone.minFee) / (zone.maxFee - zone.minFee);
  }
  
  public updateFeeDistribution(feeHistogram: FeeHistogramEntry[]): void {
    // Reset zone densities
    for (const zone of this.zones.values()) {
      zone.density = 0;
    }
    
    // Calculate total transaction count
    const totalCount = feeHistogram.reduce((sum, entry) => sum + entry.count, 0);
    
    if (totalCount === 0) return;
    
    // Distribute counts to zones
    for (const entry of feeHistogram) {
      for (const [zoneName, zone] of this.zones.entries()) {
        if (entry.feeRate >= zone.minFee && entry.feeRate < zone.maxFee) {
          zone.density += entry.count / totalCount;
          break;
        }
      }
    }
    
    // Update zone visualizations
    this.updateZoneColors();
  }
  
  private updateZoneColors(): void {
    this.zoneVisualization.children.forEach((child) => {
      if (child instanceof THREE.Mesh && child.userData['zone']) {
        const zoneName = child.userData['zone'];
        const zone = this.zones.get(zoneName);
        
        if (zone && child.material instanceof THREE.MeshBasicMaterial) {
          // Update color intensity based on density
          const intensity = 0.3 + zone.density * 0.7;
          const color = zone.color.clone().multiplyScalar(intensity);
          
          child.material.color.copy(color);
          child.material.opacity = this.zoneBoundaries.opacity * (0.5 + zone.density);
        }
      }
    });
  }
  
  public getZoneGradient(): THREE.DataTexture {
    return this.feeGradientTexture;
  }
  
  public setZoneBoundariesVisible(visible: boolean): void {
    this.zoneBoundaries.visible = visible;
    this.zoneVisualization.visible = visible;
  }
  
  public setZoneBoundaryOpacity(opacity: number): void {
    this.zoneBoundaries.opacity = Math.max(0, Math.min(1, opacity));
    
    this.zoneVisualization.children.forEach((child) => {
      if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshBasicMaterial) {
        const zoneName = child.userData['zone'];
        const zone = this.zones.get(zoneName);
        
        if (zone) {
          child.material.opacity = this.zoneBoundaries.opacity * (0.5 + zone.density);
        }
      }
    });
  }
  
  public update(deltaTime: number): void {
    this.time += deltaTime;
    
    // Animate zone boundaries with subtle pulsing
    const pulseIntensity = 0.1 + Math.sin(this.time * 0.5) * 0.05;
    
    this.zoneVisualization.children.forEach((child) => {
      if (child instanceof THREE.Mesh && child.userData['zone']) {
        const zoneName = child.userData['zone'];
        const zone = this.zones.get(zoneName);
        
        if (zone && child.material instanceof THREE.MeshBasicMaterial) {
          // Apply pulsing effect based on zone activity
          const activityPulse = 1.0 + zone.density * pulseIntensity;
          child.scale.setScalar(activityPulse);
        }
      }
    });
  }
  
  public getZoneInfo(): Array<{
    name: string;
    feeRange: [number, number];
    color: string;
    density: number;
    radius: number;
  }> {
    const info: Array<{
      name: string;
      feeRange: [number, number];
      color: string;
      density: number;
      radius: number;
    }> = [];
    
    for (const [name, zone] of this.zones.entries()) {
      info.push({
        name,
        feeRange: [zone.minFee, zone.maxFee === Infinity ? 1000 : zone.maxFee],
        color: `#${zone.color.getHexString()}`,
        density: zone.density,
        radius: zone.radius,
      });
    }
    
    return info.sort((a, b) => a.feeRange[0] - b.feeRange[0]);
  }
  
  public dispose(): void {
    // Dispose gradient texture
    this.feeGradientTexture.dispose();
    
    // Dispose zone visualization materials and geometries
    this.zoneVisualization.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.geometry) child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
}